import { useMemo, useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, Link, MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SendIcon from "@mui/icons-material/Send";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchSignatures } from "@/api/signatures";
import { useAuth } from "@/auth/AuthContext";
import {
  errorMessage, sendReply, type EmailThread, type SendAs, type ThreadAttachment, type ThreadMessage,
} from "@/api/emailThreads";
import { RichTextEditor } from "@/components/email/RichTextEditor";

// Matches Gmail::ReplySender::MAX_ATTACHMENT_BYTES (Gmail's 25 MB limit less
// base64 overhead). Inline images count too, but the server has the final say.
const MAX_ATTACHMENT_BYTES = 18 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type ComposeMode = "reply" | "reply_all" | "forward";

// Part ids repeat across messages ("1", "2"…), so a thread file is keyed by both.
const fileKey = (a: ThreadAttachment) => `${a.message_id}:${a.part_id}`;

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function lower(a: string | null | undefined) {
  return (a ?? "").trim().toLowerCase();
}

// Who a reply goes to, Gmail-style: Reply-To if the sender set one, else the
// sender; Reply all adds everyone else on the message. ap@ and the reviewer
// are never prefilled (a personal reply CCs ap@ on the server anyway).
function initialRecipients(msg: ThreadMessage, mode: ComposeMode, apInbox: string, me: string) {
  const skip = new Set([lower(apInbox), lower(me)]);
  const keep = (list: string[]) => Array.from(new Set(list.map(lower))).filter((a) => a && !skip.has(a));
  if (mode === "forward") return { to: [] as string[], cc: [] as string[] };

  // A message we sent: replying to it continues with its recipients.
  const fromUs = skip.has(lower(msg.from_address));
  const primary = fromUs
    ? msg.to.map((a) => a.email)
    : (msg.reply_to.length > 0 ? msg.reply_to.map((a) => a.email) : [msg.from_address ?? ""]);
  const to = keep(primary);
  if (mode === "reply") return { to, cc: [] };

  const others = [...msg.to, ...msg.cc].map((a) => a.email);
  return { to, cc: keep(others).filter((a) => !to.includes(a)) };
}

function subjectFor(msg: ThreadMessage, mode: ComposeMode) {
  const base = msg.subject ?? "";
  if (mode === "forward") return /^fwd?:/i.test(base) ? base : `Fwd: ${base}`;
  return /^re:/i.test(base) ? base : `Re: ${base}`;
}

function AddressField({ label, value, onChange, autoFocus }: {
  label: string; value: string[]; onChange: (v: string[]) => void; autoFocus?: boolean;
}) {
  const [input, setInput] = useState("");
  const commit = (raw: string) => {
    const parts = raw.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return;
    onChange(Array.from(new Set([...value, ...parts])));
    setInput("");
  };
  return (
    <Autocomplete
      multiple
      freeSolo
      options={[] as string[]}
      value={value}
      inputValue={input}
      onInputChange={(_e, v, reason) => {
        if (reason === "input" && /[,;\s]$/.test(v)) commit(v);
        else setInput(v);
      }}
      onChange={(_e, v) => onChange(v.map((x) => x.trim()).filter(Boolean))}
      renderTags={(tags, getTagProps) => tags.map((tag, i) => {
        const { key, ...rest } = getTagProps({ index: i });
        return <Chip key={key} {...rest} size="small" label={tag} color={EMAIL_RE.test(tag) ? "default" : "error"} />;
      })}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label={label}
          autoFocus={autoFocus}
          onBlur={() => commit(input)}
        />
      )}
    />
  );
}

export function ReplyComposer({ emailId, thread, message, mode, onClose }: {
  emailId: number;
  thread: EmailThread;
  message: ThreadMessage;
  mode: ComposeMode;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canSendAsSelf = !!user?.can_reply_as_self;
  const initial = useMemo(
    () => initialRecipients(message, mode, thread.ap_inbox, user?.email ?? ""),
    [message, mode, thread.ap_inbox, user?.email],
  );

  const [sentAs, setSentAs] = useState<SendAs>("ap");
  const [to, setTo] = useState<string[]>(initial.to);
  const [cc, setCc] = useState<string[]>(initial.cc);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCcBcc, setShowCcBcc] = useState(initial.cc.length > 0);
  const [subject, setSubject] = useState(subjectFor(message, mode));
  const [html, setHtml] = useState("");
  const [empty, setEmpty] = useState(true);
  const [files, setFiles] = useState<File[]>([]);
  // Forwarding carries the message's own attachments by default.
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(mode === "forward" ? message.attachments.map(fileKey) : []),
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const threadFiles: ThreadAttachment[] = useMemo(
    () => thread.messages.flatMap((m) => m.attachments),
    [thread.messages],
  );
  const pickedFiles = threadFiles.filter((a) => picked.has(fileKey(a)));
  const totalBytes = files.reduce((n, f) => n + f.size, 0) + pickedFiles.reduce((n, a) => n + (a.size ?? 0), 0);
  const overLimit = totalBytes > MAX_ATTACHMENT_BYTES;
  const badAddresses = [...to, ...cc, ...bcc].filter((a) => !EMAIL_RE.test(a));

  // The writer's default signature is added below their message (not in the
  // editor, so switching it never touches what they typed).
  const signatures = useQuery({ queryKey: ["email-signatures"], queryFn: fetchSignatures, staleTime: 60_000 });
  const [signatureId, setSignatureId] = useState<number | "none" | null>(null);
  const chosenSignature = signatureId === "none"
    ? null
    : (signatures.data ?? []).find((s) => (signatureId == null ? s.default : s.id === signatureId)) ?? null;
  const bodyWithSignature = chosenSignature ? `${html}<br><div>${chosenSignature.body_html}</div>` : html;

  const send = useMutation({
    mutationFn: () => sendReply(emailId, {
      sentAs, to, cc, bcc, subject, bodyHtml: bodyWithSignature, inReplyTo: message.id, files,
      threadAttachments: pickedFiles.map((a) => ({ message_id: a.message_id, part_id: a.part_id })),
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["email-thread", emailId] });
      onClose();
    },
  });

  const addFiles = (list: FileList | File[] | null) => {
    if (!list) return;
    setFiles((prev) => [...prev, ...Array.from(list)]);
  };

  const blocked = to.length === 0 || badAddresses.length > 0 || empty || overLimit || send.isPending;
  const title = mode === "forward" ? "Forward" : mode === "reply_all" ? "Reply all" : "Reply";
  const [fullScreen, setFullScreen] = useState(false);

  return (
    <Dialog open onClose={send.isPending ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="span">{title}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            to {message.from_name || message.from_address} · {message.subject}
          </Typography>
        </Box>
        <Tooltip title={fullScreen ? "Exit full screen" : "Full screen"}>
          <IconButton onClick={() => setFullScreen((v) => !v)} aria-label={fullScreen ? "Exit full screen" : "Full screen"}>
            {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose} disabled={send.isPending} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          // Images dropped on the editor go inline and other files there are
          // attached by the editor itself; a drop anywhere else is an attachment.
          if (e.defaultPrevented) return;
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        sx={{ position: "relative", ...(dragging ? { outline: "2px dashed", outlineColor: "primary.main", outlineOffset: -8 } : {}) }}
      >
        <Stack spacing={1.5}>
          <TextField
            select
            variant="standard"
            label="From"
            value={sentAs}
            onChange={(e) => setSentAs(e.target.value as SendAs)}
            helperText={sentAs === "me"
              ? `Sends from your Gmail through the company Google account (nothing to link). ${thread.ap_inbox} is CC'd so the thread stays complete.`
              : undefined}
          >
            <MenuItem value="ap">AP inbox ({thread.ap_inbox})</MenuItem>
            <MenuItem value="me" disabled={!canSendAsSelf}>
              Me ({user?.email}){canSendAsSelf ? "" : " · Freedom Power accounts only"}
            </MenuItem>
          </TextField>

          <Stack direction="row" alignItems="flex-end" spacing={1}>
            <Box sx={{ flex: 1 }}><AddressField label="To" value={to} onChange={setTo} autoFocus={mode === "forward"} /></Box>
            {!showCcBcc && <Link component="button" type="button" variant="body2" onClick={() => setShowCcBcc(true)}>Cc/Bcc</Link>}
          </Stack>
          {showCcBcc && (
            <>
              <AddressField label="Cc" value={cc} onChange={setCc} />
              <AddressField label="Bcc" value={bcc} onChange={setBcc} />
            </>
          )}
          <TextField variant="standard" label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />

          <RichTextEditor
            autoFocus={mode !== "forward"}
            onChange={(h, isEmpty) => { setHtml(h); setEmpty(isEmpty); }}
            onError={setLocalError}
            onAttach={addFiles}
            minHeight={fullScreen ? 420 : 180}
          />
          <Typography variant="caption" color="text.secondary">
            Paste or drop images into the message to put them inline. The message you're answering is quoted below your reply.
          </Typography>

          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                select
                size="small"
                variant="standard"
                label="Signature"
                value={signatureId ?? (chosenSignature?.id ?? "none")}
                onChange={(e) => setSignatureId(e.target.value === "none" ? "none" : Number(e.target.value))}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="none">No signature</MenuItem>
                {(signatures.data ?? []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}{s.default ? " (default)" : ""}</MenuItem>
                ))}
              </TextField>
              <Link href="/profile" target="_blank" rel="noopener" variant="caption">Manage signatures</Link>
            </Stack>
            {chosenSignature && (
              <Box
                sx={{
                  mt: 1, pl: 1.5, borderLeft: 3, borderColor: "divider", color: "text.secondary", fontSize: 14,
                  "& p": { my: 0.25 }, "& img": { maxWidth: "100%", height: "auto" },
                }}
                // The user's own signature, sanitized by the server when saved.
                dangerouslySetInnerHTML={{ __html: chosenSignature.body_html }}
              />
            )}
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button size="small" startIcon={<AttachFileIcon />} onClick={() => fileInput.current?.click()}>Attach files</Button>
              <Typography variant="caption" color={overLimit ? "error" : "text.secondary"}>
                {totalBytes > 0 ? `${formatBytes(totalBytes)} of 18 MB` : "or drop files anywhere here"}
              </Typography>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
            </Stack>
            {files.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                {files.map((f, i) => (
                  <Chip
                    key={`${f.name}-${i}`}
                    size="small"
                    icon={<AttachFileIcon />}
                    label={`${f.name} · ${formatBytes(f.size)}`}
                    onDelete={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {threadFiles.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Include files from this thread</Typography>
              <Stack>
                {threadFiles.map((a) => (
                  <FormControlLabel
                    key={fileKey(a)}
                    control={(
                      <Checkbox
                        size="small"
                        checked={picked.has(fileKey(a))}
                        onChange={(e) => setPicked((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(fileKey(a));
                          else next.delete(fileKey(a));
                          return next;
                        })}
                      />
                    )}
                    label={<Typography variant="body2">{a.filename} <Typography component="span" variant="caption" color="text.secondary">{formatBytes(a.size)}</Typography></Typography>}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {localError && <Alert severity="warning" onClose={() => setLocalError(null)}>{localError}</Alert>}
          {badAddresses.length > 0 && <Alert severity="error">Not an email address: {badAddresses.join(", ")}</Alert>}
          {overLimit && <Alert severity="error">Attachments are over Gmail's limit. Remove some files.</Alert>}
          {send.isError && <Alert severity="error">{errorMessage(send.error)}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Sends from {sentAs === "me" ? user?.email : thread.ap_inbox} to {to.length + cc.length + bcc.length} recipient{to.length + cc.length + bcc.length === 1 ? "" : "s"}.
        </Typography>
        <Button onClick={onClose} disabled={send.isPending}>Discard</Button>
        <Button
          variant="contained"
          startIcon={send.isPending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={blocked}
          onClick={() => send.mutate()}
        >
          {send.isPending ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
